import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Eye, EyeOff, Lock, User, Mail, Phone, Circle, PlayCircle, Target, LifeBuoy, LogOut } from "react-feather"
import { Footer } from "../../../Footer/Footer"
import { Header } from "../../Header/Header"
import { useAuthState, useAuthDispatch } from "../../../../helper/Context/context"
import { logout } from "../../../../helper/Context/action"
import { GET_M_User_SelectAPI } from "../../../../helper/Redux/Slices/UserCreationSlice/UserCreationSlice"
import { LogoLoader } from "../../../../helper/Loaders/LogoLoader"
import "./WebinarMyAccount.css"

const initialPasswords = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
}

const validatePasswords = (passwords) => {
    const errors = {}

    if (!passwords.currentPassword) {
        errors.currentPassword = "Enter your current password."
    }
    if (!passwords.newPassword) {
        errors.newPassword = "Enter a new password."
    } else if (passwords.newPassword.length < 8) {
        errors.newPassword = "New password must be at least 8 characters."
    } else if (passwords.newPassword === passwords.currentPassword) {
        errors.newPassword = "Your new password must differ from your current password."
    }
    if (!passwords.confirmPassword) {
        errors.confirmPassword = "Confirm your new password."
    } else if (passwords.newPassword !== passwords.confirmPassword) {
        errors.confirmPassword = "Passwords do not match."
    }

    return errors
}

export const WebinarMyAccount = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const authDispatch = useAuthDispatch()
    const userDetails = useAuthState()

    const { year, Month, Language } = userDetails
    const { UserID, token } = userDetails?.user || {}
    const user = userDetails?.user

    const [passwords, setPasswords] = useState(initialPasswords)
    const [errors, setErrors] = useState({})
    const [touched, setTouched] = useState({})
    const [submitted, setSubmitted] = useState(false)
    const [message, setMessage] = useState("")
    const [visiblePasswords, setVisiblePasswords] = useState({})
    const [isAuthenticated, setIsAuthenticated] = useState(
        () => Boolean(sessionStorage.getItem("A454A"))
    )
    const returnTo = ["/AllWebinarVideos", "/AllSeminarVideos"].includes(location.state?.returnTo)
        ? location.state.returnTo
        : "/AllWebinarVideos"
    const cameFromSeminars = returnTo === "/AllSeminarVideos"
    const accountVideoCounts = { all: 5, unpaid: 3, paid: 2 }

    useEffect(() => {
        handleGetData()
    }, [year?.Label, Month?.ID, Language?.Label])

    const handleGetData = () => {

        const data = {
            UserID: UserID,
            SearchTerm: "",
            token: token,
        }

        dispatch(GET_M_User_SelectAPI({ data }))
    }

    const { isLoading, TableData } = useSelector((state) => state.GET_M_User_SelectData)
    const profile = user?.profile || {}

    const handleChange = (event) => {
        const { name, value } = event.target
        const updatedPasswords = { ...passwords, [name]: value }
        setPasswords(updatedPasswords)
        setTouched((currentTouched) => ({ ...currentTouched, [name]: true }))
        setErrors(validatePasswords(updatedPasswords))
        setMessage("")
    }

    const handleLogout = () => {
        logout(authDispatch)
        setIsAuthenticated(false)
        navigate(`${returnTo}?filter=all`, { replace: true })
    }

    const openVideoFilter = (filter) => {
        navigate(filter === "all" ? returnTo : `${returnTo}?filter=${filter}`)
    }

    const handleBlur = (event) => {
        setTouched((currentTouched) => ({
            ...currentTouched,
            [event.target.name]: true,
        }))
        setErrors(validatePasswords(passwords))
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        const nextErrors = validatePasswords(passwords)
        setSubmitted(true)
        setErrors(nextErrors)
        if (Object.keys(nextErrors).length === 0) {
            setMessage("Your passwords passed validation, but the password-change service is not connected. No password has been changed.")
        }
    }

    const passwordField = (name, label, autoComplete) => {
        const showError = Boolean(errors[name] && (touched[name] || submitted))

        return (
            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor={`account-${name}`}>
                    {label}
                </label>
                <div className="input-group">
                    <input
                        id={`account-${name}`}
                        name={name}
                        type={visiblePasswords[name] ? "text" : "password"}
                        className={`form-control${showError ? " is-invalid" : ""}`}
                        autoComplete={autoComplete}
                        value={passwords[name]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={showError}
                        aria-describedby={showError ? `account-${name}-error` : undefined}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setVisiblePasswords((current) => ({
                            ...current,
                            [name]: !current[name],
                        }))}
                        aria-label={visiblePasswords[name] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                        aria-pressed={Boolean(visiblePasswords[name])}
                    >
                        {visiblePasswords[name] ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>
                {showError && (
                    <div id={`account-${name}-error`} className="invalid-feedback d-block" role="alert">
                        {errors[name]}
                    </div>
                )}
            </div>
        )
    }

    const accountContent = (
        <section className="webinar-account-card" aria-labelledby="webinar-account-heading">
            {isAuthenticated ? (
                <>
                    <div className="webinar-account-section-title">
                        <User size={18} />
                        <h2>Account Details</h2>
                    </div>
                    <div className="webinar-account-details">
                        {TableData && TableData?.length > 0 ? (
                            TableData?.map((item) => {
                                const fullName = (item?.firstName && item?.lastName)
                                    ? `${item.firstName} ${item.lastName}`
                                    : ("")
                                const userName = item?.userName || ""
                                // const accountId = item?.userID || item?.UserID || item?.tableUserID || item?.TableUserID || profile.TableUserID || user?.UserID || sessionStorage.getItem("A454A") || ""
                                const emailId = item?.emailID || ""
                                const contactNumber = item?.contactNumber || ""

                                const userDetailsList = [
                                    { label: "Full Name", value: fullName, icon: <User size={17} /> },
                                    { label: "User Name", value: userName, icon: <User size={17} /> },
                                    // { label: "Account ID", value: accountId, icon: <User size={17} /> },
                                    { label: "Email ID", value: emailId, icon: <Mail size={17} /> },
                                    { label: "Contact Number", value: contactNumber, icon: <Phone size={17} /> },
                                ]

                                return userDetailsList.map(({ label, value, icon }) => (
                                    <div className="webinar-account-detail" key={label}>
                                        <span className="webinar-account-detail-icon" aria-hidden="true">{icon}</span>
                                        <span className="webinar-account-detail-label">{label}</span>
                                        <input type="text" className="form-control" value={value || ""} readOnly />
                                    </div>
                                ))
                            })
                        ) : (
                            [
                                { label: "Full Name", value: profile.UserFullName || user?.UserFullName || "", icon: <User size={17} /> },
                                { label: "User Name", value: profile.UserName || profile.Username || profile.UserID || user?.UserName || "", icon: <User size={17} /> },
                                // { label: "Account ID", value: profile.TableUserID || user?.UserID || sessionStorage.getItem("A454A") || "", icon: <User size={17} /> },
                                { label: "Email ID", value: profile.EmailID || profile.Email || profile.email || "", icon: <Mail size={17} /> },
                                { label: "Contact Number", value: profile.ContactNumber || profile.ContactNo || profile.MobileNo || profile.Mobile || "", icon: <Phone size={17} /> },
                            ].map(({ label, value, icon }) => (
                                <div className="webinar-account-detail" key={label}>
                                    <span className="webinar-account-detail-icon" aria-hidden="true">{icon}</span>
                                    <span className="webinar-account-detail-label">{label}</span>
                                    <input type="text" className="form-control" value={value || ""} readOnly />
                                </div>
                            ))
                        )}
                    </div>

                    <div className="webinar-account-section-title webinar-account-password-title">
                        <Lock size={18} />
                        <h2>Change Password</h2>
                    </div>
                    <p className="text-muted mb-4">
                        Enter your current password and choose a new password for your account.
                    </p>
                    <form onSubmit={handleSubmit} noValidate>
                        {passwordField("currentPassword", "Current Password", "current-password")}
                        {passwordField("newPassword", "New Password", "new-password")}
                        {passwordField("confirmPassword", "Confirm New Password", "new-password")}
                        {message && (
                            <p className="webinar-account-notice" role="status">{message}</p>
                        )}
                        <button type="submit" className="webinar-account-submit">
                            <Lock size={16} />
                            Change Password
                        </button>
                    </form>
                </>
            ) : (
                <p className="webinar-account-notice" role="alert">
                    Sign in through Webinar Videos or Seminar Videos to access your account.
                </p>
            )}
            <button
                type="button"
                className="webinar-account-back"
                onClick={() => navigate(returnTo)}
            >
                Back to {cameFromSeminars ? "Seminar" : "Webinar"} Videos
            </button>
        </section>
    )

    return (
        <>
            {isLoading && <LogoLoader />}
            <div className="container-fluid webinar-page">
                <Header actives={cameFromSeminars ? "Seminars" : "Webinars"} subactive="Events" />
                <main className="webinar-main">
                    <section
                        className="webinar-hero mb-4"
                        aria-labelledby="webinar-page-title"
                        style={{
                            backgroundImage: `url("${process.env.PUBLIC_URL}/Static/Assets/Img/Webinars/page-heading-bg.png")`,
                        }}
                    >
                        <div className="webinar-hero-copy">
                            <p className="webinar-eyebrow"><span /> MILSO ACADEMY</p>
                            <h1 id="webinar-page-title">My Account</h1>
                            <p>View and edit your personal details and<br className="d-none d-md-block" /> reset your password.</p>
                        </div>
                    </section>
                    {/* {isAuthenticated && (
                        <div className="webinar-logout">
                            <button type="button" onClick={handleLogout}>Logout</button>
                        </div>
                    )} */}
                    <div className="webinar-layout row">
                        <aside className="col-12 col-xl-2 col-lg-3 webinar-sidebar-wrapper" aria-label="Webinar navigation">
                            <div className="webinar-sidebar">
                                <nav className="webinar-filter-list" aria-label="Filter videos">
                                    <button type="button" onClick={() => openVideoFilter("all")}>
                                        <PlayCircle size={16} />
                                        <span>All Videos</span>
                                        <span className="webinar-count">{accountVideoCounts.all}</span>
                                    </button>
                                    <button type="button" onClick={() => openVideoFilter("unpaid")}>
                                        <Circle size={15} />
                                        <span>Unpaid</span>
                                        <span className="webinar-count">{accountVideoCounts.unpaid}</span>
                                    </button>
                                    <button type="button" onClick={() => openVideoFilter("paid")}>
                                        <Lock size={15} />
                                        <span>Paid</span>
                                        <span className="webinar-count">{accountVideoCounts.paid}</span>
                                    </button>
                                </nav>

                                <div className="webinar-quick-links">
                                    <h2><span className="webinar-quick-icon"><Target size={15} /></span> Quick Access</h2>
                                    <button type="button" className="active" aria-current="page">
                                        <User size={16} />
                                        <span>My Account</span>
                                    </button>
                                    <a href="/Contact">
                                        <LifeBuoy size={16} />
                                        <span>Contact Support</span>
                                    </a>
                                    <button type="button" onClick={handleLogout}><LogOut size={16} /><span>Logout</span></button>
                                </div>
                                <div
                                    className="webinar-sidebar-art"
                                    aria-hidden="true"
                                    style={{
                                        backgroundImage: `url("${process.env.PUBLIC_URL}/Static/Assets/Img/Webinars/sidebr-bg.png")`,
                                    }}
                                />
                            </div>
                        </aside>
                        <section className="webinar-results col-12 col-xl-10 col-lg-9" aria-label="Account details">
                            {accountContent}
                        </section>
                    </div>
                </main>
                <Footer />
            </div>
        </>
    )
}
